import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { API_URL } from "../data/site";

const GARAGE_INTEREST = "Sebring Garage — Storage & Race-Week Space";

const INTERESTS = [
  "Arrive-and-Drive Race Program",
  GARAGE_INTEREST,
  "Driver Development & Coaching",
  "Race Logistics & Vehicle Transport",
  "Trackside Operational Support",
  "Performance Optimization",
  "General Inquiry",
];

const inputCls =
  "w-full border border-neutral-300 bg-white px-4 py-3 text-sm text-[#111111] placeholder:text-neutral-400 transition-colors duration-300 focus:border-[#111111] focus:outline-none";

export const InquiryForm = ({ compact = false }) => {
  const [params] = useSearchParams();
  const initialInterest = params.get("interest") === "sebring-garage" ? GARAGE_INTEREST : INTERESTS[0];
  const [form, setForm] = useState({ name: "", email: "", phone: "", interest: initialInterest, track: "", preferred_contact: "Email", message: "", company: "", form_ts: Date.now() });
  const [sending, setSending] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API_URL}/inquiries`, form);
      toast.success("Inquiry received. We'll be in touch within one business day.");
      setForm({ name: "", email: "", phone: "", interest: initialInterest, track: "", preferred_contact: "Email", message: "", company: "", form_ts: Date.now() });
    } catch (err) {
      toast.error("Something went wrong sending your inquiry. Please email us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <form data-testid="inquiry-form" onSubmit={submit} className="space-y-5" noValidate={false}>
      <div className={compact ? "space-y-5" : "grid gap-5 sm:grid-cols-2"}>
        <div>
          <label htmlFor="inquiry-name" className="font-overline mb-2 block text-[10px] uppercase tracking-[0.25em] text-neutral-500">
            Full Name *
          </label>
          <input
            id="inquiry-name"
            data-testid="inquiry-name-input"
            type="text"
            required
            autoComplete="name"
            value={form.name}
            onChange={update("name")}
            placeholder="Your name"
            className={inputCls}
          />
        </div>
        <div>
          <label htmlFor="inquiry-email" className="font-overline mb-2 block text-[10px] uppercase tracking-[0.25em] text-neutral-500">
            Email *
          </label>
          <input
            id="inquiry-email"
            data-testid="inquiry-email-input"
            type="email"
            required
            autoComplete="email"
            value={form.email}
            onChange={update("email")}
            placeholder="you@example.com"
            className={inputCls}
          />
        </div>
      </div>

      <div className={compact ? "space-y-5" : "grid gap-5 sm:grid-cols-2"}>
        <div>
          <label htmlFor="inquiry-phone" className="font-overline mb-2 block text-[10px] uppercase tracking-[0.25em] text-neutral-500">
            Phone
          </label>
          <input
            id="inquiry-phone"
            data-testid="inquiry-phone-input"
            type="tel"
            autoComplete="tel"
            value={form.phone}
            onChange={update("phone")}
            placeholder="Optional"
            className={inputCls}
          />
        </div>
        <div>
          <label htmlFor="inquiry-interest" className="font-overline mb-2 block text-[10px] uppercase tracking-[0.25em] text-neutral-500">
            I'm Interested In *
          </label>
          <select
            id="inquiry-interest"
            data-testid="inquiry-interest-select"
            required
            value={form.interest}
            onChange={update("interest")}
            className={inputCls}
          >
            {INTERESTS.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className={compact ? "space-y-5" : "grid gap-5 sm:grid-cols-2"}>
        <div>
          <label htmlFor="inquiry-track" className="font-overline mb-2 block text-[10px] uppercase tracking-[0.25em] text-neutral-500">
            Track / Event <span className="normal-case tracking-normal text-neutral-400">(if applicable)</span>
          </label>
          <input
            id="inquiry-track"
            data-testid="inquiry-track-input"
            type="text"
            value={form.track}
            onChange={update("track")}
            placeholder="e.g. Sebring, WRL weekend"
            className={inputCls}
          />
        </div>
        <div>
          <label htmlFor="inquiry-preferred" className="font-overline mb-2 block text-[10px] uppercase tracking-[0.25em] text-neutral-500">
            Preferred Contact Method
          </label>
          <select
            id="inquiry-preferred"
            data-testid="inquiry-preferred-select"
            value={form.preferred_contact}
            onChange={update("preferred_contact")}
            className={inputCls}
          >
            <option value="Email">Email</option>
            <option value="Phone">Phone</option>
          </select>
        </div>
      </div>

      <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", top: "-9999px", height: 0, overflow: "hidden" }}>
        <label htmlFor="inquiry-company">Company</label>
        <input
          id="inquiry-company"
          data-testid="inquiry-company-honeypot"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={form.company}
          onChange={update("company")}
        />
      </div>

      <div>
        <label htmlFor="inquiry-message" className="font-overline mb-2 block text-[10px] uppercase tracking-[0.25em] text-neutral-500">
          Your Program Goals *
        </label>
        <textarea
          id="inquiry-message"
          data-testid="inquiry-message-input"
          required
          minLength={10}
          rows={compact ? 4 : 6}
          value={form.message}
          onChange={update("message")}
          placeholder="Tell us about your car, class, target circuits, and what you want from the season."
          className={`${inputCls} resize-y`}
        />
      </div>

      <button
        type="submit"
        data-testid="inquiry-submit-button"
        disabled={sending}
        className="group inline-flex w-full items-center justify-center gap-2 bg-[#111111] px-8 py-4 text-[13px] font-semibold uppercase tracking-[0.16em] text-white transition-colors duration-300 hover:bg-[#e63946] disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
      >
        {sending ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" /> Sending
          </>
        ) : (
          <>
            Send Inquiry
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </>
        )}
      </button>
      <p className="text-xs leading-relaxed text-neutral-500">
        Your details are used only to respond to this inquiry. We reply within one business day.
      </p>
    </form>
  );
};
